import { BidPriceSetting } from 'components/BidPrice/BidPriceSetting';
import { CollapsibleSection } from 'components/CollapsibleSection';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { FieldArray, connect } from 'formik';
import i18next from 'i18n';
import _ from 'lodash';
import { FC, useCallback } from 'react';
import styles from './agencyForm.module.scss';
import {
  getChannelGroupName,
  getEnabledOptimizes,
  getVisibleAdTypes,
  isAdTypeEnabled
} from './agencyAddonVisibility';
import { AddonSectionPlaceholder } from './AddonSectionPlaceholder';

const AgencyBidPriceSettingsForm: FC<any> = (props) => {
  const { addonFeatures } = props.formik.values;

  const renderBidPriceSettings = useCallback(
    ({ form, name }) => {
      const currency = _.get(form.values, 'currency');
      const bidPriceSettings = _.get(form.values, name) ?? [];

      // 以 adTypeId（無則 type 名稱）建立 form 出價設定的索引查找表
      const settingIndexMap = new Map<
        string,
        { setting: any; index: number }
      >();
      bidPriceSettings.forEach((setting: any, index: number) => {
        const settingKey = setting.adTypeId
          ? setting.adTypeId.toString()
          : setting.type;
        settingIndexMap.set(settingKey, { setting, index });
      });

      const renderBidPriceSetting = ({
        setting,
        index,
        optimizes
      }: {
        setting: any;
        index: number;
        optimizes: string[];
      }) => {
        const type = setting.type;
        let key = `${type}`;
        if (setting.adTypeId) {
          key = `${key}-${setting.adTypeId}`;
        }
        return (
          <BidPriceSetting
            key={key}
            fieldName={`${name}.${index}`}
            type={type}
            currency={currency}
            optimizes={optimizes}
            showDetail
          />
        );
      };

      // 是否有任何 channel 下 ad type 為開（決定 placeholder 文字要用哪一種）
      let hasAnyEnabledAdType = false;

      // 渲染條件：channel 開 → 該 channel 內已啟用的 ad type → 該 ad type 在
      // form 中找得到 bid-price 設定 → 設定的 optimizes 過濾後至少留下一個。
      // 任一層為空就略過。
      const sections = ChannelGroupSetting.activeValues()
        .map((channelGroup) => {
          const items = getVisibleAdTypes(addonFeatures, channelGroup)
            .filter((adTypeSetting) => {
              const enabled = isAdTypeEnabled(addonFeatures, adTypeSetting);
              if (enabled) hasAnyEnabledAdType = true;
              return enabled;
            })
            .map((adTypeSetting) => {
              const adTypeKey = adTypeSetting.adTypeId
                ? adTypeSetting.adTypeId.toString()
                : adTypeSetting.name;
              const entry = settingIndexMap.get(adTypeKey);
              if (!entry) return undefined;
              const optimizes = getEnabledOptimizes(
                addonFeatures,
                channelGroup,
                Object.keys(entry.setting.autoBidCap)
              );
              if (optimizes.length === 0) return undefined;
              return { ...entry, optimizes };
            })
            .filter(
              (
                item
              ): item is { setting: any; index: number; optimizes: string[] } =>
                item !== undefined
            );
          if (items.length === 0) {
            return null;
          }
          return (
            <CollapsibleSection
              key={channelGroup.config.id}
              title={getChannelGroupName(channelGroup)}
            >
              <div className={styles.bidPriceSettings}>
                {items.map(renderBidPriceSetting)}
              </div>
            </CollapsibleSection>
          );
        })
        .filter(Boolean);

      if (sections.length === 0) {
        // 已有 ad type 開但無對應計價模式開 → 提示用戶開啟計價模式
        // 否則 → 提示用戶開啟廣告活動類型
        const placeholderParent = hasAnyEnabledAdType
          ? i18next.t('agencyDetail.labels.priceModelTab')
          : i18next.t('agencyDetail.labels.adTypeTab');
        return (
          <AddonSectionPlaceholder
            message={i18next.t('agency.placeholders.addon', {
              parent: placeholderParent,
              child: i18next.t('agency.addonItem.campaign.bidPriceConstraint')
            })}
          />
        );
      }
      return (
        <fieldset>
          <legend>
            <span>{i18next.t('agencyDetail.labels.bidPriceSettingsTab')}</span>
          </legend>
          <div className={styles.sectionsShadow}>{sections}</div>
        </fieldset>
      );
    },
    [addonFeatures]
  );

  const renderBidPriceFieldArray = useCallback(
    () => (
      <FieldArray
        name='addonProps.campaignBidPrice'
        render={renderBidPriceSettings}
      />
    ),
    [renderBidPriceSettings]
  );

  return <div className={styles.agencyFrom}>{renderBidPriceFieldArray()}</div>;
};

export default connect(AgencyBidPriceSettingsForm);
